/** Run one activity's own surface module on a canvas in this page. */

import { useEffect, useRef } from "react";

import { apiBaseUrl } from "../../lib/env";
import type { CanvasState, StateEntity } from "../../lib/runtime";

interface Drawing {
  draw(entities: StateEntity[], stale: boolean, elapsedMs: number): void;
}

interface SurfaceModule {
  start(handle: { canvas: HTMLCanvasElement }): Drawing;
}

/** How long a snapshot may go unrefreshed before the surface is told it is drawing stale state. */
const STALE_MS = 4000;

export function useSurface(
  activityId: string,
  name: string,
  state: CanvasState | null,
): {
  canvas: React.RefObject<HTMLCanvasElement | null>;
  failed: string | null;
} {
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const held = useRef<{ entities: StateEntity[]; at: number }>({ entities: [], at: 0 });
  const failed = useRef<string | null>(null);

  held.current = {
    entities: state?.entities ?? held.current.entities,
    at: state === null ? held.current.at : performance.now(),
  };

  useEffect(() => {
    let live = true;
    let frame = 0;
    let url: string | null = null;

    async function run(): Promise<void> {
      const path = `/v1/canvas/surfaces/${encodeURIComponent(activityId)}/${encodeURIComponent(name)}`;
      const response = await fetch(`${apiBaseUrl}${path}`);
      if (!response.ok) {
        throw new Error(`the surface module is not being served (${String(response.status)})`);
      }
      url = URL.createObjectURL(new Blob([await response.text()], { type: "text/javascript" }));
      const module = (await import(/* @vite-ignore */ url)) as SurfaceModule;
      const target = canvas.current;
      if (!live || target === null) return;

      const drawing = module.start({ canvas: target });
      const tick = (elapsedMs: number) => {
        if (!live) return;
        fit(target);
        const snapshot = held.current;
        drawing.draw(snapshot.entities, elapsedMs - snapshot.at > STALE_MS, elapsedMs);
        frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);
    }

    // The console, never the canvas: this one is on air.
    run().catch((error: unknown) => {
      failed.current = error instanceof Error ? error.message : "the surface would not load";
      console.warn(`[canvas] ${failed.current}`);
    });

    return () => {
      live = false;
      if (frame !== 0) cancelAnimationFrame(frame);
      if (url !== null) URL.revokeObjectURL(url);
    };
  }, [activityId, name]);

  return { canvas, failed: failed.current };
}

/** Match the drawing buffer to the element, so nothing is drawn at the wrong scale. */
function fit(canvas: HTMLCanvasElement): void {
  const ratio = window.devicePixelRatio || 1;
  const width = Math.round(canvas.clientWidth * ratio);
  const height = Math.round(canvas.clientHeight * ratio);
  if (canvas.width !== width) canvas.width = width;
  if (canvas.height !== height) canvas.height = height;
}
